import { createFileRoute, Link } from "@tanstack/react-router";
import { CourseCard, ComingSoonCard } from "@/components/learning/CourseCard";
import { AlertTriangle, ArrowRight, BookOpen01, User01 } from "@untitledui/icons";
import { useListUserEnrollments } from "@/services/api/learning-enrollments/learning-enrollments";
import { useCatalogGetCourses } from "@/services/api/catalog/catalog";
import { useState } from "react";
import { CourseEvaluationModal } from "@/features/learning/components/CourseEvaluationModal";
import { MyLearningPaths } from "@/features/learning-paths/components/MyLearningPaths";
import { MyIltBookings } from "@/features/instructor-led/components/MyIltBookings";
import { AvailableToYou } from "@/features/seat-orders/components/AvailableToYou";
import { MIN_CARDS_TO_DISPLAY } from "@/constants/ui";
import { useAuthStore } from "@/stores/authStore";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { toNum } from "@/utils/course-formatters";
import { Avatar } from "@/components/base/avatar/avatar";
import { CourseDeliveryType } from "@/types/domain";
import { saveCourseCertificate } from "@/services/certificate-client";

export const Route = createFileRoute("/_app/my-learning")({
  component: MyLearning,
});

function MyLearning() {
  const { user } = useAuthStore();
  const [evaluationCourse, setEvaluationCourse] = useState<{ id: string; title: string } | null>(
    null,
  );

  const {
    data: enrollmentsData,
    isLoading: isLoadingEnrollments,
    isError: isEnrollmentsError,
  } = useListUserEnrollments();
  const enrollments = enrollmentsData?.items ?? [];

  const {
    data: catalog,
    isLoading: isLoadingCatalog,
    isError: isCatalogError,
  } = useCatalogGetCourses({
    page: 1,
    pageSize: 3,
  });

  const hasEnrollments = (enrollments?.length ?? 0) > 0;
  const isProfileIncomplete = !user?.localAuthority || !user?.sector;

  return (
    <>
      {/* Full Width Gradient for Header */}
      <div className="relative box-border flex size-full flex-col content-stretch items-center px-0 pt-0 pb-[40px]">
        <div className="relative mb-[-40px] box-border flex w-full shrink-0 flex-col content-stretch items-start gap-[8px] overflow-clip">
          <div className="bg-arc-primary-50 h-[184px] w-full shrink-0" />
        </div>
        {/* Header Content constrained by max-w and px-8 */}
        <div className="relative mb-[-40px] box-border flex w-full max-w-[1280px] shrink-0 flex-col content-stretch items-start gap-[24px] px-[32px] py-0">
          <div className="relative flex w-full shrink-0 content-stretch items-start gap-[20px]">
            {/* Avatar */}
            <div className="bg-arc-base-white flex size-[160px] shrink-0 items-center justify-center rounded-full border border-solid border-[rgba(0,0,0,0.08)] p-[6px]">
              <Avatar
                size="2xl"
                src={user?.profileImageUrl}
                alt={`${user?.firstName ?? user?.email ?? "User"}'s profile`}
                placeholder={<User01 className="text-arc-gray-400 size-[72px]" />}
                className="size-full"
                contrastBorder
              />
            </div>
            <div className="relative box-border flex min-h-px min-w-px flex-[1_0_0] shrink-0 flex-col content-stretch items-start gap-[16px] px-0 pt-[64px] pb-0">
              <div className="relative flex w-full shrink-0 flex-col flex-wrap content-start items-start gap-[4px]">
                <h1 className="text-arc-gray-900 relative shrink-0 text-[48px] leading-[60px] font-[var(--font-fira-sans)] font-semibold tracking-[-0.96px] not-italic">
                  {isProfileIncomplete ? "Welcome!" : "My Learning"}
                </h1>
                {isProfileIncomplete ? (
                  <Link
                    to="/profile"
                    className="bg-utility-warning-50 ring-utility-warning-200 hover:bg-utility-warning-100 flex items-center gap-1 rounded-full py-1 pr-2 pl-1 ring-1 transition duration-100 ease-linear ring-inset"
                  >
                    <span className="bg-utility-warning-500 ring-utility-warning-500 flex items-center justify-center rounded-full p-[5px] ring-1 ring-inset">
                      <AlertTriangle className="text-arc-gray-700 size-3" />
                    </span>
                    <span className="text-arc-gray-700 text-xs font-medium">
                      Complete your profile
                    </span>
                    <ArrowRight className="text-arc-gray-700 size-4" />
                  </Link>
                ) : (
                  <p className="text-arc-gray-600 font-body relative shrink-0 text-[16px] leading-[24px] font-normal">
                    {user?.displayName || user?.firstName} • {user?.email}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="bg-arc-gray-200 relative h-px w-full shrink-0" />
        </div>
      </div>

      {/* Main content area (My Courses, etc.) - white background, constrained width */}
      <main className="mx-auto max-w-[1280px] px-8 py-8">
        <div className="space-y-8">
          {/* Upcoming face-to-face training bookings: hidden while the ilt flag is off */}
          <MyIltBookings />

          {/* My learning paths: hidden while the learning-paths flag is off */}
          <MyLearningPaths />

          {/* Restricted courses the learner was granted to buy seats on (LMS-196): hidden when there are none */}
          <AvailableToYou />

          {/* My Courses Section */}
          <section>
            <h2 className="text-arc-gray-900 mb-6 text-4xl font-semibold">My Courses</h2>

            {isLoadingEnrollments ? (
              <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-arc-gray-100 h-64 animate-pulse rounded-2xl" />
                ))}
              </div>
            ) : isEnrollmentsError ? (
              <div className="text-arc-error-600 py-8 text-center">
                Failed to load enrollments. Please try again.
              </div>
            ) : hasEnrollments ? (
              <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                {enrollments.map((enrollment) => {
                  const percentComplete = toNum(enrollment.percentComplete);
                  const isCompleted = percentComplete >= 100;

                  // An instructor-led course is completed by attending a session (LMS-139). Its certificate is
                  // offered straight away, with no evaluation first, and its page is the face-to-face training one.
                  if (toNum(enrollment.deliveryType) === CourseDeliveryType.InstructorLed) {
                    const courseId = enrollment.courseId ?? "";
                    return (
                      <CourseCard
                        key={enrollment.enrollmentId}
                        title={enrollment.courseTitle ?? "Untitled Course"}
                        description={enrollment.shortDescription ?? ""}
                        imageUrl={enrollment.heroImageUrl ?? ""}
                        progress={percentComplete}
                        completedAt={enrollment.completedAt}
                        href={`/face-to-face-training/${courseId}`}
                        actionLabel={isCompleted ? "Download certificate" : "View course"}
                        onAction={
                          isCompleted
                            ? () => {
                                // Failures are shown as toasts by the certificate client.
                                saveCourseCertificate(courseId, enrollment.courseTitle).catch(
                                  () => undefined,
                                );
                              }
                            : undefined
                        }
                      />
                    );
                  }

                  const hasEvaluated = !!enrollment.evaluationCompletedAt;
                  const showEvaluation = isCompleted && !hasEvaluated;

                  return (
                    <CourseCard
                      key={enrollment.enrollmentId}
                      title={enrollment.courseTitle ?? "Untitled Course"}
                      description={enrollment.shortDescription ?? ""}
                      imageUrl={enrollment.heroImageUrl ?? ""}
                      progress={percentComplete}
                      evaluationCompletedAt={enrollment.evaluationCompletedAt}
                      href={`/courses/${enrollment.courseId}`}
                      onAction={
                        showEvaluation
                          ? () =>
                              setEvaluationCourse({
                                id: enrollment.courseId ?? "",
                                title: enrollment.courseTitle ?? "",
                              })
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <EmptyState>
                <EmptyState.Header>
                  <EmptyState.FeaturedIcon icon={BookOpen01} color="gray" />
                </EmptyState.Header>
                <EmptyState.Content>
                  <EmptyState.Title>No courses</EmptyState.Title>
                  <EmptyState.Description>
                    You haven't started any courses yet.
                  </EmptyState.Description>
                </EmptyState.Content>
              </EmptyState>
            )}
          </section>
        </div>
      </main>

      <CourseEvaluationModal
        isOpen={!!evaluationCourse}
        onClose={() => setEvaluationCourse(null)}
        courseId={evaluationCourse?.id ?? ""}
        courseTitle={evaluationCourse?.title ?? ""}
      />

      {/* All Courses Section - Edge to Edge */}
      <section className="bg-arc-surface py-section-sm md:py-section">
        <div className="mx-auto max-w-[1280px] px-8">
          <h2 className="text-arc-gray-900 mb-2 text-4xl font-semibold">All Courses</h2>
          <p className="text-arc-gray-600 mb-8 text-xl">
            Not sure where to start? Have a look at our curated categories.
          </p>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {isLoadingCatalog ? (
              [1, 2, 3].map((i) => (
                <div key={i} className="bg-arc-gray-200 h-64 animate-pulse rounded-2xl" />
              ))
            ) : isCatalogError ? (
              <div className="text-arc-error-600 col-span-3 py-8 text-center">
                Failed to load course catalog. Please try again.
              </div>
            ) : !catalog?.items || catalog.items.length === 0 ? (
              <div className="text-arc-gray-600 col-span-3 text-center">
                No courses available at the moment.
              </div>
            ) : (
              <>
                {catalog.items.map((course) => (
                  <CourseCard
                    key={course.id}
                    title={course.title ?? "Untitled"}
                    description={course.shortDescription}
                    imageUrl={course.heroImageUrl}
                    actionLabel="View course"
                    href={`/courses/${course.id}`}
                  />
                ))}
                {/* Add placeholder cards to fill the grid */}
                {Array.from({
                  length: Math.max(0, MIN_CARDS_TO_DISPLAY - catalog.items.length),
                }).map((_, index) => (
                  <ComingSoonCard key={`coming-soon-${index}`} />
                ))}
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
